import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CurrentLang } from 'utils/lang';
import { paymentFailureHandler } from '../../actions/payment';
import { setStripeSubmitting } from '../../actions/stripe';
import { updatePurchase, receivePayment, getPurchase } from '../../actions/purchase';
import stripeEngine from '../../../payments/core/engines/stripe';
import useWhitelabelEnvs from '../whitelabel/useWhitelabelEnvs';

/**
 * useStripeExpressWallet hook.
 * Orchestrate Redux state, error handling, and payment handlers for a Stripe express wallet
 * (Google Pay, Apple Pay, …). Composes the wallet's SDK hook (`useWalletSdk`) for all Express
 * Checkout Element lifecycle management. The wallet is selected through parameters so the same
 * orchestration serves any wallet without duplication.
 *
 * Event split (mirrors the Adyen express wallet gating pattern):
 * - `click` is the validation gate: it forwards `{ resolve, reject, registerPurchaserSave }` to
 *   onPaymentButtonClick so Checkout can run purchaser-form/bulk validations before the wallet
 *   sheet opens, and defer the purchaser PUT to confirm when the form is valid but unsaved.
 * - `confirm` executes the payment: deferred purchaser save → ConfirmationToken from the ECE
 *   Elements instance → paymentAttempt (create-session: unconfirmed PaymentIntent →
 *   client_secret) → stripeEngine.confirmPayment, which dismisses the wallet sheet and runs any
 *   3DS challenge inline (no separate handleNextAction step for wallets).
 * - `cancel` (user dismissed the sheet) releases the purchase without flagging an error.
 *
 * @param {object} props - Wallet wiring and the checkout gate callback.
 * @param {Function} props.onPaymentButtonClick - Callback when the wallet button is clicked;
 *   receives `{ resolve, reject, registerPurchaserSave }` to gate the wallet sheet on checkout
 *   validations.
 * @param {boolean} props.enabled - Whether this wallet is enabled for the tenant.
 * @param {string} props.paymentType - Stripe payment type (e.g. `google_pay`, `apple_pay`).
 * @param {Function} props.paymentAttempt - Redux thunk action creator for this wallet's attempt.
 * @param {Function} props.useWalletSdk - Wallet-bound SDK hook (e.g. `useStripeGooglePaySdk`).
 * @param {Function} [props.beforeWalletPayment] - Optional current Quick Purchase check before dispatch.
 * @returns {object} Express wallet ECE state and container ref.
 */
function useStripeExpressWallet({
  onPaymentButtonClick,
  beforeWalletPayment,
  enabled,
  paymentType,
  paymentAttempt,
  useWalletSdk,
}) {
  const dispatch = useDispatch();
  const beforeWalletPaymentRef = useRef(beforeWalletPayment);
  beforeWalletPaymentRef.current = beforeWalletPayment;
  const requestedPayment = useRef(false);
  // Holds the purchaser-save thunk handed back by Checkout's gate when the purchaser form changed
  // but is valid: the wallet sheet opens immediately and the PUT is run (and awaited) at confirm
  // time, since the sheet can't reopen after async work.
  const savePurchaserRef = useRef(null);

  const env = useWhitelabelEnvs();
  const currency = env?.currency;
  const stripeAppearance = env?.stripe?.appearance;
  const purchaseToken = useSelector((state) => state.purchase.get('token'));
  const purchaseTotal = useSelector((state) => state.purchase.get('total'));
  // The purchaser name/email/phone live in the redux-form `purchaser` form
  // (purchaserFirstName/purchaserLastName/email/phone), NOT in purchase state — the same source
  // the rest of the funnel reads (e.g. useMercadoPagoTDCDynamicValues).
  const purchaser = useSelector((state) => state.form?.purchaser?.values || {});
  const isUpdating = useSelector((state) => state.purchase.get('isUpdating'));
  const fetchingPayment = useSelector((state) => state.payment.get('isFetching'));
  const stripeLocale = CurrentLang();

  const createStripeError = useCallback(
    (error) => {
      return {
        paymentEngine: 'stripe',
        paymentType,
        errorReason: error?.code || 'default',
        message: error?.message || 'Stripe express wallet payment error',
      };
    },
    [paymentType],
  );

  const buildBillingDetails = useCallback(() => {
    const billingDetails = {};
    const purchaserName = [purchaser?.purchaserFirstName, purchaser?.purchaserLastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (purchaserName) billingDetails.name = purchaserName;
    if (purchaser?.email) billingDetails.email = purchaser.email;
    if (purchaser?.phone) billingDetails.phone = purchaser.phone;

    return billingDetails;
  }, [
    purchaser?.purchaserFirstName,
    purchaser?.purchaserLastName,
    purchaser?.email,
    purchaser?.phone,
  ]);

  /**
   * Handles the ECE `click` event (validation gate; the token is NOT created here).
   * Stripe requires calling `event.resolve()` to open the wallet sheet; not resolving aborts.
   * `reject` is provided for contract compatibility with the Adyen express wallet flow.
   * `registerPurchaserSave` lets Checkout defer the purchaser PUT to the confirm handler when the
   * form changed but is valid, so the sheet can open synchronously instead of being rejected.
   * @param {Object} event - ECE click event with `resolve`.
   */
  const handleClick = useCallback(
    (event) => {
      // Reset so a stale thunk from a previous click never leaks into this attempt.
      savePurchaserRef.current = null;
      onPaymentButtonClick({
        resolve: () => event.resolve(),
        reject: () => {
          // Not calling event.resolve() keeps the wallet sheet closed; nothing else to do.
        },
        registerPurchaserSave: (savePurchaser) => {
          savePurchaserRef.current = typeof savePurchaser === 'function' ? savePurchaser : null;
        },
      });
    },
    [onPaymentButtonClick],
  );

  /**
   * Handles the ECE `confirm` event: submits the ECE Elements instance (required by Stripe
   * before tokenizing it), creates the ConfirmationToken from that instance, runs the payment
   * attempt (create-session: the backend creates the PaymentIntent unconfirmed and returns its
   * client_secret), and finalizes with `stripeEngine.confirmPayment`.
   * That client-side confirmation is the only signal that dismisses the wallet sheet, and it runs
   * any required 3DS challenge inline (issuer-driven on Google Pay FPAN cards or requested by the
   * backend via request_three_d_secure), so there is no separate handleNextAction step here.
   * On success the purchase polling is started directly (receivePayment + getPurchase); the
   * terminal purchase state is owned by the Stripe webhook + PurchaseComplete's isStripePending.
   * @param {Object} event - ECE confirm event; `paymentFailed` reports failures natively in-sheet.
   */
  const handleConfirm = useCallback(
    async (event) => {
      /**
       * Resolves the confirm event with a native failure so the wallet sheet closes.
       * Pre-submission failures must go through this — until confirmPayment runs (which
       * counts as the event's one submission), it is the only signal that releases the sheet.
       * @returns {void}
       */
      const releaseSheetWithFailure = () => {
        if (typeof event?.paymentFailed === 'function') {
          event.paymentFailed({ reason: 'fail' });
        }
      };

      // A duplicate confirm is left alone on purpose: the in-flight attempt owns this event and
      // settles it, so releasing here would resolve it twice.
      if (requestedPayment.current) return;

      // The click gate applies these same conditions, but it runs when the button is pressed and
      // this event fires seconds later, after the user picked a card in the sheet — a purchase
      // update landing in between would otherwise strand the sheet in its processing state.
      if (isUpdating || fetchingPayment || !purchaseToken) {
        releaseSheetWithFailure();
        return;
      }

      requestedPayment.current = true;
      // Same client-side loading contract as Stripe TDC: stripeSubmitting covers the
      // createConfirmationToken window without unmounting the ECE; cleared in the finally,
      // from then on isUpdating/fetchingPayment (set by the action) take over.
      dispatch(setStripeSubmitting(true));

      try {
        // Required by Stripe before createConfirmationToken (docs.stripe.com/elements/
        // express-checkout-element/accept-a-payment): submits the ECE Elements instance's
        // internal state. Skipping this leaves the instance unsubmitted, and confirmPayment
        // later fails as an unrecoverable client-side IntegrationError — no network request is
        // ever made, so nothing about it is visible in Stripe's dashboard or our own logs.
        const submitResult = await stripeEngine.submitElements({
          elements: stripeEngine.getExpressElements(),
        });
        if (submitResult?.error) {
          releaseSheetWithFailure();
          paymentFailureHandler(dispatch, createStripeError(submitResult.error));
          return;
        }

        // Persist the purchaser the gate deferred when the form was valid but not yet saved (the
        // wallet sheet opened synchronously, so the PUT could not run before resolve). Must complete
        // before the payment attempt so the backend has the latest purchaser data; abort on failure
        // (updatePurchaseFields already surfaces the error).
        if (savePurchaserRef.current) {
          const savePurchaser = savePurchaserRef.current;
          savePurchaserRef.current = null;
          try {
            await savePurchaser();
          } catch (error) {
            // The PUT failed before anything reached Stripe: paymentFailed is the only
            // signal that closes the sheet (updatePurchaseFields already surfaced the error).
            releaseSheetWithFailure();
            return;
          }
        }

        const billingDetails = buildBillingDetails();
        const confirmationResult = await stripeEngine.createConfirmationToken(
          {
            payment_method_data: {
              billing_details: billingDetails,
            },
          },
          { elements: stripeEngine.getExpressElements() },
        );

        if (confirmationResult?.error || !confirmationResult?.confirmationToken?.id) {
          releaseSheetWithFailure();
          paymentFailureHandler(
            dispatch,
            createStripeError(
              confirmationResult?.error || { message: 'Stripe confirmation token was not created' },
            ),
          );
          return;
        }

        const { id: confirmationTokenId, payment_method_preview: paymentMethodPreview } =
          confirmationResult.confirmationToken;
        const card = paymentMethodPreview?.card;
        const walletInfo = card
          ? {
              brand: card.brand,
              funding: card.funding,
              expMonth: card.exp_month,
              expYear: card.exp_year,
              last4: card.last4,
              country: card.country,
              holderName: billingDetails.name,
            }
          : { holderName: billingDetails.name };

        if (beforeWalletPaymentRef.current) {
          try {
            await beforeWalletPaymentRef.current();
          } catch (error) {
            releaseSheetWithFailure();
            return;
          }
        }

        const paymentResult = await dispatch(
          paymentAttempt(purchaseToken, {
            confirmationTokenId,
            walletInfo,
          }),
        );

        if (!paymentResult || paymentResult.outcome === 'failed') {
          // Backend-declined attempt: nothing was submitted to Stripe, so the sheet only
          // closes through paymentFailed (the attempt action already surfaced the error).
          releaseSheetWithFailure();
          return;
        }

        if (paymentResult.outcome === 'requires_client_confirmation') {
          const confirmResult = await stripeEngine.confirmPayment({
            clientSecret: paymentResult.clientAction?.clientSecret,
            confirmParams: {
              confirmation_token: confirmationTokenId,
              // Fallback for redirect-based methods only; wallets resolve without redirecting.
              return_url: `${window.location.origin}/purchase/${purchaseToken}/complete?trackPurchase=true`,
            },
            redirect: 'if_required',
          });

          if (confirmResult?.error) {
            // Calling confirmPayment already counts as the confirm event's one submission,
            // so Stripe.js resolves the sheet from its result — invoking event.paymentFailed
            // here is a protocol violation ("Unexpected call to paymentFailed()").
            paymentFailureHandler(dispatch, createStripeError(confirmResult.error));
          } else {
            // Stripe.js closed the sheet (and ran any required 3DS challenge inline). Kick off
            // the purchase polling directly: the purchase is still an `attempt` until the Stripe
            // webhook finalizes it, and PurchaseComplete's isStripePending drives the polling
            // (engine `stripe` + `attempt`). This must NOT go through paymentPollingHandler, whose
            // pending branch only fires getPurchase when `using3dSecure` is set — that would force
            // the backend to flag 3DS on every wallet payment just to resume polling.
            dispatch(receivePayment(paymentResult.raw?.payload));
            dispatch(getPurchase(purchaseToken));
          }
        }
      } finally {
        requestedPayment.current = false;
        dispatch(setStripeSubmitting(false));
      }
    },
    [
      buildBillingDetails,
      createStripeError,
      dispatch,
      fetchingPayment,
      isUpdating,
      paymentAttempt,
      purchaseToken,
    ],
  );

  /**
   * Handles the ECE `cancel` event: the user dismissed the wallet sheet.
   * Releases the purchase and the submitting flag without flagging an error
   * (same as the CANCELED status of Adyen express wallets).
   */
  const handleCancel = useCallback(() => {
    dispatch(updatePurchase(false));
    dispatch(setStripeSubmitting(false));
  }, [dispatch]);

  return useWalletSdk({
    enabled,
    amount: purchaseTotal,
    currency,
    appearance: stripeAppearance,
    locale: stripeLocale,
    onClick: handleClick,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  });
}

export default useStripeExpressWallet;
